import { createTheme } from "@mui/material";
import BigNumber from "bignumber.js";
import { EstateTypeNameEnum } from "common/enums/EstateTypeEnum";
import { StatusNameEnum } from "common/enums/StatusEnum";
import { squareToTsubo } from "common/functions/utilities";
import { onAutocompleteChange } from "common/interfaces/AutocompleteHandlers";
import { JsonMaster } from "common/interfaces/JsonMaster";
import { Estate } from "graphql/API";
import { useBuildPriceHooks } from "hooks/buildPriceHooks";
import { sum } from "lodash";
import {
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { PricingDialogProps } from "./interfaces";

export const usePricingDialogHooks = (props: PricingDialogProps) => {
  const mainTheme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontFamily: `"Lato", zen-maru-gothic, sans-serif`,
            fontWeight: 700
          }
        }
      },

      MuiSwitch: {
        styleOverrides: {
          thumb: {
            border: "solid rgba(170, 160, 130, 1) 1px"
          },
          track: {
            border: "solid rgba(170, 160, 130, 1) 1px"
          },
          switchBase: {
            "&.Mui-checked": {
              color: "white"
            },
            "&.Mui-checked + .MuiSwitch-track": {
              backgroundColor: "rgba(255, 240, 0, 1)",
              opacity: 1
            }
          }
        }
      },

      MuiChip: {
        styleOverrides: {
          label: {
            textOverflow: "unset",
            fontFamily: `"Lato", zen-maru-gothic, sans-serif`
          }
        }
      },

      MuiAutocomplete: {
        styleOverrides: {
          endAdornment: { top: "calc(50% - 1px)" },
          popper: {
            border: "2px solid black !important",
            borderRadius: "10px !important"
          }
        }
      }
    }
  });

  const titRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  const [selectedEstateIds, setSelectedEstateIds] = useState<Set<string>>(
    new Set()
  );
  const [tsuboArea, setTsuboArea] = useState<number | undefined>(undefined);
  const [averageUnitPrice, setAverageUnitPrice] = useState<number | undefined>(
    undefined
  );
  const [structure, setStructure] = useState<string | null | undefined>(
    undefined
  );
  const [usefulLifeYears, setUsefulLifeYears] = useState<number | undefined>(
    undefined
  );
  const [remainRate, setRemainRate] = useState<number | undefined>(undefined);
  const [year, setYear] = useState<number | undefined>(undefined);
  const [buildPriceOfTsubo, setBuildPriceOfTsubo] = useState<
    number | undefined
  >(undefined);
  const [forceRemainRateTo10, setForceRemainRateTo10] =
    useState<boolean>(false);
  const [squareArea, setSquareArea] = useState<number | undefined>(undefined);

  const { getBuildPrice } = useBuildPriceHooks(props.setFatal);

  const comparison = (a: Estate, b: Estate) =>
    (b.status === StatusNameEnum.公開 ||
    b.status === StatusNameEnum.非公開 ||
    b.updatedAt === undefined ||
    b.updatedAt === null
      ? Number.MIN_VALUE
      : new Date(b.updatedAt).getTime()) -
      (a.status === StatusNameEnum.公開 ||
      a.status === StatusNameEnum.非公開 ||
      a.updatedAt === undefined ||
      a.updatedAt === null
        ? Number.MIN_VALUE
        : new Date(a.updatedAt).getTime()) ||
    (b.unitPrice || 0) - (a.unitPrice || 0);

  const publicLandEstates = useMemo(
    () =>
      props.estatesForPricing.filter(
        (estate) =>
          (estate.estateType === EstateTypeNameEnum.土地 ||
            estate.estateType === EstateTypeNameEnum.分譲地) &&
          (estate.status === StatusNameEnum.公開 ||
            estate.status === StatusNameEnum.非公開)
      ),
    [props.estatesForPricing]
  );

  const soldLandEstates = useMemo(
    () =>
      props.estatesForPricing.filter(
        (estate) =>
          (estate.estateType === EstateTypeNameEnum.土地 ||
            estate.estateType === EstateTypeNameEnum.分譲地) &&
          (estate.status === StatusNameEnum.商談中 ||
            estate.status === StatusNameEnum.成約済み)
      ),
    [props.estatesForPricing]
  );

  const allLandEstates = useMemo(
    () =>
      [...soldLandEstates, ...publicLandEstates].toSorted((a, b) =>
        comparison(a, b)
      ),

    [publicLandEstates, soldLandEstates]
  );

  const publicHouseEstates = useMemo(
    () =>
      props.estatesForPricing.filter(
        (estate) =>
          (estate.estateType === EstateTypeNameEnum.中古戸建 ||
            estate.estateType === EstateTypeNameEnum.新築建売) &&
          (estate.status === StatusNameEnum.公開 ||
            estate.status === StatusNameEnum.非公開)
      ),
    [props.estatesForPricing]
  );

  const soldHouseEstates = useMemo(
    () =>
      props.estatesForPricing.filter(
        (estate) =>
          (estate.estateType === EstateTypeNameEnum.中古戸建 ||
            estate.estateType === EstateTypeNameEnum.新築建売) &&
          (estate.status === StatusNameEnum.商談中 ||
            estate.status === StatusNameEnum.成約済み)
      ),
    [props.estatesForPricing]
  );

  const allHouseEstates = useMemo(
    () =>
      [...soldHouseEstates, ...publicHouseEstates].toSorted((a, b) =>
        comparison(a, b)
      ),
    [publicHouseEstates, soldHouseEstates]
  );

  const publicMansionEstates = useMemo(
    () =>
      props.estatesForPricing.filter(
        (estate) =>
          estate.estateType === EstateTypeNameEnum.マンション &&
          (estate.status === StatusNameEnum.公開 ||
            estate.status === StatusNameEnum.非公開)
      ),
    [props.estatesForPricing]
  );

  const soldMansionEstates = useMemo(
    () =>
      props.estatesForPricing.filter(
        (estate) =>
          estate.estateType === EstateTypeNameEnum.マンション &&
          (estate.status === StatusNameEnum.商談中 ||
            estate.status === StatusNameEnum.成約済み)
      ),
    [props.estatesForPricing]
  );

  const allMansionEstates = useMemo(
    () =>
      [...soldMansionEstates, ...publicMansionEstates].toSorted((a, b) =>
        comparison(a, b)
      ),
    [publicMansionEstates, soldMansionEstates]
  );

  const calcAverageUnitPrice = useCallback(
    (newSelectedEstateIds: Set<string>) => {
      if (newSelectedEstateIds.size === 0) {
        return 0;
      }

      const selectedEstates = props.estatesForPricing.filter((estate) =>
        newSelectedEstateIds.has(estate.id)
      );

      const totalUnitPrice = sum(
        selectedEstates.map((estate) => estate.unitPrice || 0)
      );

      return Math.round((totalUnitPrice / selectedEstates.length) * 10) / 10;
    },
    [props.estatesForPricing]
  );

  const landAmount = useMemo(() => {
    if (tsuboArea && averageUnitPrice) {
      return Math.round(tsuboArea * averageUnitPrice);
    }
    return 0;
  }, [averageUnitPrice, tsuboArea]);

  const calcRemainYears = useCallback(
    (newUsefulLifeYears: number | undefined, newYear: number | undefined) =>
      Math.max(
        0,
        (newUsefulLifeYears || 0) - (new Date().getFullYear() - (newYear || 0))
      ),
    []
  );

  const remainYears = useMemo(
    () => calcRemainYears(usefulLifeYears, year),
    [calcRemainYears, usefulLifeYears, year]
  );

  const houseAmount = useMemo(() => {
    if (
      buildPriceOfTsubo &&
      squareArea &&
      (remainRate || forceRemainRateTo10)
    ) {
      const tsuboArea = squareToTsubo(squareArea);

      return Math.round(
        (buildPriceOfTsubo *
          tsuboArea *
          (forceRemainRateTo10 === true ? 10 : (remainRate ?? 0))) /
          100 /
          10000
      );
    }
    return 0;
  }, [buildPriceOfTsubo, squareArea, remainRate, forceRemainRateTo10]);

  const calcRemainRate = useCallback(
    (newUsefulLifeYears: number | undefined, newYear: number | undefined) =>
      newUsefulLifeYears && newYear
        ? Math.round(
            Math.max(
              0,
              Math.min(
                1,
                calcRemainYears(newUsefulLifeYears, newYear) /
                  newUsefulLifeYears
              )
            ) * 100
          )
        : 0,
    [calcRemainYears]
  );

  const invokeGetBuildPrice = useCallback(
    async (param: {
      newStructure: string | null | undefined;
      newYear: number | undefined;
    }) => {
      const { newStructure, newYear } = param;

      if (newStructure && newYear) {
        const gotBuildPrice = await getBuildPrice(newYear);

        const buildPrice =
          newStructure === "木造（W）"
            ? gotBuildPrice?.unitPriceW
            : newStructure === "鉄骨鉄筋コンクリート造（SRC）"
              ? gotBuildPrice?.unitPriceSRC
              : newStructure === "鉄筋コンクリート造（RC）"
                ? gotBuildPrice?.unitPriceRC
                : newStructure === "鉄骨造（S）"
                  ? gotBuildPrice?.unitPriceS
                  : newStructure === "コンクリートブロック造（CB）"
                    ? gotBuildPrice?.unitPriceCB
                    : newStructure === "その他"
                      ? gotBuildPrice?.unitPriceOther
                      : undefined;

        const buildPriceOfTsubo = buildPrice
          ? new BigNumber(buildPrice)
              .times(3.305785)
              .div(1000)
              .dp(0, BigNumber.ROUND_FLOOR)
              .times(1000)
              .toNumber()
          : 0;

        setBuildPriceOfTsubo(buildPriceOfTsubo);
      }
    },
    [getBuildPrice]
  );

  const onSelectEstateClick = useCallback(
    (estateId: string) => {
      const newSelectedEstateIds = new Set(selectedEstateIds);

      if (newSelectedEstateIds.has(estateId)) {
        newSelectedEstateIds.delete(estateId);
      } else {
        newSelectedEstateIds.add(estateId);
      }

      setSelectedEstateIds(newSelectedEstateIds);

      const newAverageUnitPrice = calcAverageUnitPrice(newSelectedEstateIds);

      setAverageUnitPrice(newAverageUnitPrice);
    },
    [calcAverageUnitPrice, selectedEstateIds]
  );

  const onTsuboAreaChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(event.target.value);

      if (isNaN(value)) {
        setTsuboArea(undefined);
      } else {
        setTsuboArea(value);
      }
    },
    []
  );

  const onAveragePriceChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(event.target.value);

      if (isNaN(value)) {
        setAverageUnitPrice(undefined);
      } else {
        setAverageUnitPrice(value);
      }
    },
    []
  );

  const onStructureChange: onAutocompleteChange<JsonMaster | string> =
    useCallback((_, value) => {
      const structureName = (value as JsonMaster | null)?.name;
      const newUsefulLifeYears =
        structureName === "木造（W）"
          ? 22
          : structureName === "鉄骨鉄筋コンクリート造（SRC）"
            ? 47
            : structureName === "鉄筋コンクリート造（RC）"
              ? 47
              : structureName === "鉄骨造（S）"
                ? 27
                : structureName === "コンクリートブロック造（CB）"
                  ? 38
                  : undefined;

      setStructure(structureName);

      if (newUsefulLifeYears !== undefined) {
        setUsefulLifeYears(newUsefulLifeYears);
      }
    }, []);

  const onStructureBlur = useCallback(async () => {
    await invokeGetBuildPrice({ newStructure: structure, newYear: year });
  }, [invokeGetBuildPrice, structure, year]);

  const onUsefulLifeYearsChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(event.target.value);

      if (isNaN(value)) {
        setUsefulLifeYears(undefined);
      } else {
        const newRemainRate = calcRemainRate(value, year);
        setUsefulLifeYears(value);
        setRemainRate(newRemainRate);
      }
    },
    [calcRemainRate, year]
  );

  const onRemainRateChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(event.target.value);

      if (isNaN(value)) {
        setRemainRate(undefined);
      } else {
        setRemainRate(value);
      }
    },
    []
  );

  const onYearChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(event.target.value);

      if (isNaN(value)) {
        setYear(undefined);
      } else {
        const newRemainRate = calcRemainRate(usefulLifeYears, value);
        setYear(value);
        setRemainRate(newRemainRate);
      }
    },
    [calcRemainRate, usefulLifeYears]
  );

  const onYearBlur = useCallback(async () => {
    await invokeGetBuildPrice({ newStructure: structure, newYear: year });
  }, [invokeGetBuildPrice, structure, year]);

  const onBuildPriceChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(event.target.value);

      if (isNaN(value)) {
        setBuildPriceOfTsubo(undefined);
      } else {
        setBuildPriceOfTsubo(value);
      }
    },
    []
  );

  const onForceRemainRateTo10Change = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setForceRemainRateTo10(checked);
    },
    []
  );

  const onSquareAreaChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = parseFloat(event.target.value);

      if (isNaN(value)) {
        setSquareArea(undefined);
      } else {
        setSquareArea(value);
      }
    },
    []
  );

  const onOkButtonClick = useCallback(() => {
    props.onOkButtonClick?.();
    if (props.onClose) {
      props.onClose();
    }
  }, [props]);

  const onBackPaperClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (titRef.current && boxRef.current) {
        if (
          titRef.current.contains(event.target as Node) ||
          boxRef.current.contains(event.target as Node)
        ) {
          return;
        }

        if (props.onClose) {
          props.onClose();
        }
      }
    },
    [props]
  );

  useEffect(() => {
    if (!props.open) {
      setTimeout(() => {
        setSelectedEstateIds(new Set());
        setTsuboArea(undefined);
        setAverageUnitPrice(0);

        setStructure(undefined);
        setUsefulLifeYears(undefined);
        setRemainRate(undefined);
        setYear(undefined);
        setBuildPriceOfTsubo(undefined);
        setForceRemainRateTo10(false);
        setSquareArea(undefined);
      }, 0);
    }
  }, [props.open]);

  useEffect(() => {
    if (props.open && props.estatesForPricing.length > 0) {
      const initialSelectedIds = new Set(
        props.estatesForPricing
          .filter(
            (estate) =>
              (estate.estateType === EstateTypeNameEnum.土地 ||
                estate.estateType === EstateTypeNameEnum.分譲地) &&
              (estate.tsuboArea ?? 0) > 0 &&
              (estate.tsuboArea ?? 0) < 100
          )
          .map((estate) => estate.id)
      );
      const initialAverageUnitPrice = calcAverageUnitPrice(initialSelectedIds);

      setTimeout(() => {
        setSelectedEstateIds(initialSelectedIds);
        setAverageUnitPrice(initialAverageUnitPrice);
      }, 0);
    }
  }, [props.open, props.estatesForPricing, calcAverageUnitPrice]);

  return {
    mainTheme,

    titRef,
    boxRef,

    selectedEstateIds,

    publicLandEstates,
    soldLandEstates,
    allLandEstates,

    publicHouseEstates,
    soldHouseEstates,
    allHouseEstates,

    publicMansionEstates,
    soldMansionEstates,
    allMansionEstates,

    tsuboArea,
    averageUnitPrice,
    landAmount,
    remainYears,
    remainRate,
    structure,
    usefulLifeYears,
    year,
    buildPriceOfTsubo,
    forceRemainRateTo10,
    squareArea,
    houseAmount,

    onSelectEstateClick,
    onTsuboAreaChange,
    onAveragePriceChange,
    onStructureChange,
    onStructureBlur,
    onUsefulLifeYearsChange,
    onRemainRateChange,
    onYearChange,
    onYearBlur,
    onBuildPriceChange,
    onForceRemainRateTo10Change,
    onSquareAreaChange,
    onOkButtonClick,
    onBackPaperClick
  } as const;
};

export type UsePricingDialogHooksType = ReturnType<
  typeof usePricingDialogHooks
>;
